import api, {
  clearSelectedPlatformTenantContext,
  getSelectedPlatformTenantContext,
  setSelectedPlatformTenantContext,
} from './api';

const BASE = '/platform/tenants';

const unwrap = (response) => response?.data;

const firstError = (errors) => {
  const out = {};
  Object.entries(errors || {}).forEach(([field, messages]) => {
    out[field] = Array.isArray(messages) ? String(messages[0] || '') : String(messages || '');
  });
  return out;
};

export const mapLaravelError = (error) => {
  const payload = error?.response?.data || {};

  if (payload?.errors && typeof payload.errors === 'object') {
    return {
      message: payload.message || 'Validation failed.',
      fieldErrors: firstError(payload.errors),
      status: error?.response?.status || 422,
    };
  }

  return {
    message: payload?.message || error?.message || 'Request failed.',
    fieldErrors: {},
    status: error?.response?.status || 500,
  };
};

const appendFormData = (formData, key, value) => {
  if (value === undefined || value === null || value === '') {
    return;
  }

  if (value instanceof File) {
    formData.append(key, value);
    return;
  }

  if (typeof value === 'boolean') {
    formData.append(key, value ? '1' : '0');
    return;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      formData.append(`__empty_arrays[${key}]`, '1');
      return;
    }

    value.forEach((item, index) => {
      appendFormData(formData, `${key}[${index}]`, item);
    });
    return;
  }

  if (typeof value === 'object') {
    Object.entries(value).forEach(([childKey, childValue]) => {
      appendFormData(formData, `${key}[${childKey}]`, childValue);
    });
    return;
  }

  formData.append(key, value);
};

const buildTenantFormData = (payload) => {
  const formData = new FormData();

  Object.entries(payload || {}).forEach(([key, value]) => {
    appendFormData(formData, key, value);
  });

  return formData;
};

export const listPlatformTenants = async () => unwrap(await api.get(BASE));
export const getPlatformTenant = async (id) => unwrap(await api.get(`${BASE}/${id}`));
export const getPlatformTenantUsers = async (id, params = {}) =>
  unwrap(await api.get(`${BASE}/${id}/users`, { params }));
export const getPlatformTenantLicenses = async (id) =>
  unwrap(await api.get(`${BASE}/${id}/licenses`));
export const getPlatformTenantLicense = async (id, licenseId) =>
  unwrap(await api.get(`${BASE}/${id}/licenses/${licenseId}`));
export const createPlatformTenantLicense = async (id, payload) =>
  unwrap(await api.post(`${BASE}/${id}/licenses`, payload));
export const updatePlatformTenantLicense = async (id, licenseId, payload) =>
  unwrap(await api.put(`${BASE}/${id}/licenses/${licenseId}`, payload));
export const testPlatformTenantDatabaseConnection = async (id) =>
  unwrap(await api.post(`${BASE}/${id}/database-test`));
export const createPlatformTenant = async (payload) =>
  unwrap(await api.post(BASE, buildTenantFormData(payload), { headers: { 'Content-Type': 'multipart/form-data' } }));
export const updatePlatformTenant = async (id, payload) =>
  unwrap(await api.post(`${BASE}/${id}?_method=PUT`, buildTenantFormData(payload), { headers: { 'Content-Type': 'multipart/form-data' } }));
export const archivePlatformTenant = async (id) => unwrap(await api.delete(`${BASE}/${id}`));
export const restorePlatformTenant = async (id) => unwrap(await api.post(`${BASE}/${id}/restore`));
export const deletePlatformTenantLicense = async (id, licenseId) =>
  unwrap(await api.delete(`${BASE}/${id}/licenses/${licenseId}`));
export const selectPlatformTenant = (tenant) => setSelectedPlatformTenantContext(tenant);
export const getSelectedPlatformTenant = () => getSelectedPlatformTenantContext();
export const clearSelectedPlatformTenant = () => clearSelectedPlatformTenantContext();

const platformTenantsApi = {
  listPlatformTenants,
  getPlatformTenant,
  getPlatformTenantUsers,
  getPlatformTenantLicenses,
  getPlatformTenantLicense,
  createPlatformTenantLicense,
  updatePlatformTenantLicense,
  testPlatformTenantDatabaseConnection,
  deletePlatformTenantLicense,
  createPlatformTenant,
  updatePlatformTenant,
  archivePlatformTenant,
  restorePlatformTenant,
  selectPlatformTenant,
  getSelectedPlatformTenant,
  clearSelectedPlatformTenant,
  mapLaravelError,
};

export default platformTenantsApi;
